import { collapseGroups, curve, isFlow, keyOf, placeNodes, quantityOf, type Band, type CollapsedGroup, type PlacedNode, type Side, type WireEdge, type WireTraversal } from './graph-model';

/**
 * Mise en couches de l'explorateur (ADR-129 §4) : colonnes de gauche (amont) à droite (aval), l'objet
 * exploré au centre avec ses références au-dessus et en dessous, des groupes par relation et par type,
 * le repli au-delà de 60 nœuds visibles. Un groupe montre ses `GROUP_PAGE` premiers objets, puis une carte
 * « Afficher plus » qui porte les liens des suivants. Positions en pixels du monde, sans React.
 */

export const CARD_W = 208;
export const CARD_H = 88;
export const CENTER_W = 260;
export const CENTER_H = 176;
export const COLLAPSED_H = 68;
export const MORE_H = 56;
/** Objets montrés d'un groupe avant « Afficher plus », puis à chaque « Afficher plus » (remarque de l'utilisateur du 2026-10-15). */
export const GROUP_PAGE = 10;
const COLUMN_GAP = 128;
const ROW_GAP = 14;
const GROUP_HEADER = 22;
const GROUP_GAP = 14;
const LANE_PAD = 16;
const TOP = 52;

export interface LayoutCard {
  key: string;
  kind: 'center' | 'node' | 'collapsed' | 'more';
  /** Nœud (center, node) ou membres (collapsed). */
  node?: PlacedNode;
  group?: CollapsedGroup;
  /** Carte « Afficher plus » : le groupe et le nombre d'objets pas encore montrés. */
  more?: { group: string; remaining: number };
  column: number;
  x: number;
  y: number;
  w: number;
  h: number;
  sides: Set<Side>;
}

export interface LayoutLink {
  key: string;
  /** Arêtes de la traversée portées par ce lien (plusieurs quand un groupe est replié). */
  edges: number[];
  from: string;
  to: string;
  d: string;
  arrow: boolean;
  labelX: number;
  labelY: number;
  width: number;
}

export interface LayoutGroupHeader {
  key: string;
  group: string;
  x: number;
  y: number;
  w: number;
  count: number;
}

export interface Layout {
  width: number;
  height: number;
  placed: Map<string, PlacedNode>;
  cards: LayoutCard[];
  links: LayoutLink[];
  headers: LayoutGroupHeader[];
  collapsed: Map<string, CollapsedGroup>;
  columns: Array<{ column: number; x: number; w: number }>;
  /** Nombre de nœuds visibles (après filtre par type), objet exploré compris. */
  visibleCount: number;
}

const bandOrder: Record<Band, number> = { above: 0, flow: 1, below: 2 };

export function layoutGraph(
  traversal: WireTraversal,
  options: { hiddenTypes?: ReadonlySet<string>; threshold?: number; trace?: boolean; /** Objets montrés par groupe, `GROUP_PAGE` par défaut. */ shown?: ReadonlyMap<string, number> } = {},
): Layout {
  const placed = placeNodes(traversal);
  const startKey = keyOf(traversal.start);
  const hidden = options.hiddenTypes ?? new Set<string>();
  const visible = [...placed.values()].filter((node) => node.key === startKey || !hidden.has(node.ref.type)).sort((a, b) => a.group.localeCompare(b.group) || (a.label ?? a.key).localeCompare(b.label ?? b.key));
  // Repli au-delà du seuil, sur tous les objets visibles ; puis pagination des autres groupes : les objets au-delà de la
  // page sont portés par la carte « Afficher plus » du groupe.
  const collapsed = collapseGroups(visible, options.threshold);
  const memberOf = new Map<string, string>();
  for (const group of collapsed.values()) for (const member of group.members) memberOf.set(member, `group:${group.group}`);
  const remaining = new Map<string, number>();
  const taken = new Map<string, number>();
  const paged: PlacedNode[] = [];
  for (const node of visible) {
    const index = taken.get(node.group) ?? 0;
    taken.set(node.group, index + 1);
    if (node.key === startKey || collapsed.has(node.group) || index < Math.max(1, options.shown?.get(node.group) ?? GROUP_PAGE)) paged.push(node);
    else {
      memberOf.set(node.key, `more:${node.group}`);
      remaining.set(node.group, (remaining.get(node.group) ?? 0) + 1);
    }
  }

  // Colonnes : celles des nœuds visibles ; en voisinage, l'amont et l'aval existent toujours (couloirs vides dits).
  const columnSet = new Set<number>(visible.map((node) => node.column));
  if (options.trace !== true) {
    columnSet.add(-1);
    columnSet.add(1);
  }
  const columnList = [...columnSet].sort((a, b) => a - b);
  const columns: Layout['columns'] = [];
  let cursor = LANE_PAD;
  for (const column of columnList) {
    const w = column === 0 ? CENTER_W : CARD_W;
    columns.push({ column, x: cursor, w });
    cursor += w + COLUMN_GAP;
  }
  const width = cursor - COLUMN_GAP + LANE_PAD;
  const columnOf = new Map(columns.map((entry) => [entry.column, entry]));

  // Contenu de chaque colonne : groupes ordonnés (au-dessus, flux, en dessous), l'objet exploré entre les deux.
  type Slot = { key: string; kind: LayoutCard['kind']; node?: PlacedNode; group?: CollapsedGroup; more?: { group: string; remaining: number }; h: number };
  type Block = { group: string; band: Band; slots: Slot[]; count: number };
  const blocks = new Map<number, Block[]>();
  const seenGroups = new Set<string>();
  for (const node of paged) {
    const list = blocks.get(node.column) ?? [];
    blocks.set(node.column, list);
    if (node.key === startKey) {
      list.push({ group: 'center', band: 'flow', slots: [{ key: node.key, kind: 'center', node, h: CENTER_H }], count: 1 });
      continue;
    }
    const folded = collapsed.get(node.group);
    let block = list.find((candidate) => candidate.group === node.group);
    if (block === undefined) {
      block = { group: node.group, band: node.band, slots: [], count: 0 };
      list.push(block);
    }
    block.count += 1;
    if (block.count === 1 && folded === undefined) block.count += remaining.get(node.group) ?? 0;
    if (folded !== undefined) {
      if (!seenGroups.has(node.group)) {
        seenGroups.add(node.group);
        block.slots.push({ key: `group:${node.group}`, kind: 'collapsed', group: folded, h: COLLAPSED_H });
      }
    } else block.slots.push({ key: node.key, kind: 'node', node, h: CARD_H });
  }
  for (const [group, count] of remaining) {
    const node = paged.find((candidate) => candidate.group === group && candidate.key !== startKey);
    const block = node === undefined ? undefined : blocks.get(node.column)?.find((candidate) => candidate.group === group);
    block?.slots.push({ key: `more:${group}`, kind: 'more', more: { group, remaining: count }, h: MORE_H });
  }
  const heightOf = (list: Block[]): number => list.reduce((sum, block) => sum + (block.group === 'center' ? 0 : GROUP_HEADER) + block.slots.reduce((inner, slot) => inner + slot.h + ROW_GAP, 0) - ROW_GAP + GROUP_GAP, 0) - GROUP_GAP;
  // Au-dessus, puis l'objet exploré, puis le flux et ce qui est en dessous.
  const rank = (block: Block): number => (block.group === 'center' ? 0.5 : bandOrder[block.band]);
  for (const list of blocks.values()) list.sort((a, b) => rank(a) - rank(b) || a.group.localeCompare(b.group));
  const tallest = Math.max(CENTER_H, ...[...blocks.values()].map(heightOf));
  const height = TOP + tallest + LANE_PAD * 2;

  const cards: LayoutCard[] = [];
  const headers: LayoutGroupHeader[] = [];
  for (const [column, list] of blocks) {
    const where = columnOf.get(column)!;
    let y = TOP + (tallest - heightOf(list)) / 2;
    for (const block of list) {
      if (block.group !== 'center') {
        headers.push({ key: `header:${block.group}`, group: block.group, x: where.x, y, w: where.w, count: block.count });
        y += GROUP_HEADER;
      }
      for (const slot of block.slots) {
        const w = slot.kind === 'center' ? CENTER_W : CARD_W;
        const card: LayoutCard = { key: slot.key, kind: slot.kind, column, x: where.x + (where.w - w) / 2, y, w, h: slot.h, sides: new Set() };
        if (slot.node !== undefined) card.node = slot.node;
        if (slot.group !== undefined) card.group = slot.group;
        if (slot.more !== undefined) card.more = slot.more;
        cards.push(card);
        y += slot.h + ROW_GAP;
      }
      y += GROUP_GAP - ROW_GAP;
    }
  }

  // Liens : une arête dont un bout est replié s'attache au nœud du groupe ; deux arêtes du même couple n'en font qu'un.
  const cardOf = new Map(cards.map((card) => [card.key, card]));
  const endOf = (key: string): LayoutCard | undefined => cardOf.get(memberOf.get(key) ?? key);
  const maxQuantity = Math.max(0, ...traversal.edges.map((edge) => quantityOf(edge)?.value ?? 0));
  const merged = new Map<string, { edges: number[]; from: LayoutCard; to: LayoutCard; flow: boolean; edge: WireEdge; quantity: number }>();
  traversal.edges.forEach((edge, index) => {
    const source = endOf(keyOf(edge.source));
    const target = endOf(keyOf(edge.target));
    if (source === undefined || target === undefined || source === target) return;
    // Un lien de flux se dessine de l'amont (colonne la plus à gauche) vers l'aval ; une référence de sa source vers sa cible.
    const flow = isFlow(edge);
    const [from, to] = flow && source.column !== target.column ? (source.column < target.column ? [source, target] : [target, source]) : [source, target];
    const id = `${edge.relation_id}|${from.key}|${to.key}`;
    const entry = merged.get(id) ?? { edges: [], from, to, flow, edge, quantity: 0 };
    entry.edges.push(index);
    entry.quantity += quantityOf(edge)?.value ?? 0;
    merged.set(id, entry);
  });
  const links: LayoutLink[] = [];
  const labelSlots = new Map<string, number>();
  for (const [id, entry] of merged) {
    const { from, to } = entry;
    let d: string;
    let labelX: number;
    let labelY: number;
    if (from.column !== to.column) {
      const [left, right] = from.column < to.column ? [from, to] : [to, from];
      left.sides.add('right');
      right.sides.add('left');
      const start = { x: left.x + left.w, y: left.y + left.h / 2 };
      const end = { x: right.x, y: right.y + right.h / 2 };
      d = from === left ? curve(start, end, false) : curve(end, start, false);
      labelX = (start.x + end.x) / 2;
      labelY = (start.y + end.y) / 2;
    } else {
      const [upper, lower] = from.y < to.y ? [from, to] : [to, from];
      upper.sides.add('bottom');
      lower.sides.add('top');
      const start = { x: upper.x + upper.w / 2, y: upper.y + upper.h };
      const end = { x: lower.x + lower.w / 2, y: lower.y };
      d = from === upper ? curve(start, end, true) : curve(end, start, true);
      labelX = start.x;
      labelY = (start.y + end.y) / 2;
    }
    // Deux libellés au même endroit se décalent.
    const slot = `${Math.round(labelX / 8)}:${Math.round(labelY / 8)}`;
    const taken = labelSlots.get(slot) ?? 0;
    labelSlots.set(slot, taken + 1);
    labelY += taken * 22;
    const width = options.trace === true && maxQuantity > 0 && entry.quantity > 0 ? 2 + (12 * entry.quantity) / maxQuantity : 2;
    links.push({ key: id, edges: entry.edges, from: from.key, to: to.key, d, arrow: true, labelX, labelY, width });
  }
  return { width: Math.max(width, 640), height, placed, cards, links, headers, collapsed, columns, visibleCount: visible.length };
}
